// Hooks de React (§6.3). Leer y escribir están separados (SOLID: I): una
// pantalla que solo lee no arrastra la API de escritura.

import { createContext, useCallback, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";
import type { LocalFirstEngine, MutationCall, OpDraft, SyncStatus } from "../core/engine";
import type { RecoveryItem, ScopeState, SetDelta, Values } from "../core/types";
import type { ViewRow } from "../core/view";

const EngineContext = createContext<LocalFirstEngine | null>(null);

export function LocalFirstProvider({ engine, children }: { engine: LocalFirstEngine | null; children: ReactNode }) {
  return <EngineContext.Provider value={engine}>{children}</EngineContext.Provider>;
}

/** El motor del provider, o `null` si no hay (offline desactivado). */
export function useEngine(): LocalFirstEngine | null {
  return useContext(EngineContext);
}

const noopSubscribe = () => () => {};

/** Versión del motor: cambia con cada modificación observable. */
function useEngineVersion(engine: LocalFirstEngine | null): number {
  return useSyncExternalStore(
    engine ? (listener) => engine.subscribe(listener) : noopSubscribe,
    () => engine?.getVersion() ?? 0,
    () => engine?.getVersion() ?? 0,
  );
}

export interface LocalQueryOptions {
  filter?: (row: ViewRow) => boolean;
  sort?: (a: ViewRow, b: ViewRow) => number;
  /** 1-indexado. Sin `pageSize`, todas las filas. */
  page?: number;
  pageSize?: number;
}

export interface LocalQueryResult {
  rows: ViewRow[];
  total: number;
  state: ScopeState | undefined;
}

/**
 * Filas de un alcance con filtro, orden y paginación en memoria (v1: recalcula
 * sobre todas las filas; con pocos miles es instantáneo, §6.6).
 */
export function useLocalQuery(scope: string | null, options: LocalQueryOptions = {}): LocalQueryResult {
  const engine = useEngine();
  const version = useEngineVersion(engine);
  const { filter, sort, page = 1, pageSize } = options;
  return useMemo(() => {
    void version;
    if (!engine || !scope) return { rows: [], total: 0, state: undefined };
    let rows = engine.rows(scope);
    if (filter) rows = rows.filter(filter);
    if (sort) rows = [...rows].sort(sort);
    const total = rows.length;
    if (pageSize) rows = rows.slice((page - 1) * pageSize, page * pageSize);
    return { rows, total, state: engine.scopeState(scope) };
  }, [engine, scope, version, filter, sort, page, pageSize]);
}

export function useLocalRecord(scope: string | null, rowId: string | null): { row: ViewRow | undefined; state: ScopeState | undefined } {
  const engine = useEngine();
  const version = useEngineVersion(engine);
  return useMemo(() => {
    void version;
    if (!engine || !scope || !rowId) return { row: undefined, state: undefined };
    return { row: engine.row(scope, rowId), state: engine.scopeState(scope) };
  }, [engine, scope, rowId, version]);
}

export interface LocalMutation {
  create(set: Values, options?: { rowId?: string; sets?: Record<string, SetDelta> }): MutationCall;
  patch(rowId: string, change: { set?: Values; sets?: Record<string, SetDelta> }): MutationCall;
  remove(rowId: string): MutationCall;
  /** Grupo atómico (p. ej. un documento con sus líneas). */
  commit(drafts: OpDraft[]): MutationCall;
}

export function useLocalMutation(scope: string): LocalMutation {
  const engine = useEngine();
  const required = useCallback(() => {
    if (!engine) throw new Error("useLocalMutation sin LocalFirstProvider");
    return engine;
  }, [engine]);
  return useMemo(
    () => ({
      create: (set, options = {}) => required().commit([{ scope, kind: "create", rowId: options.rowId, set, sets: options.sets }]),
      patch: (rowId, change) => required().commit([{ scope, kind: "patch", rowId, ...change }]),
      remove: (rowId) => required().commit([{ scope, kind: "delete", rowId }]),
      commit: (drafts) => required().commit(drafts),
    }),
    [required, scope],
  );
}

const OFF: SyncStatus = { online: true, leader: false, pending: 0, recovery: 0, paused: false };

export function useSyncStatus(): SyncStatus {
  const engine = useEngine();
  const version = useEngineVersion(engine);
  return useMemo(() => {
    void version;
    return engine?.syncStatus() ?? OFF;
  }, [engine, version]);
}

export function useScopeStatus(scope: string | null): ScopeState | undefined {
  const engine = useEngine();
  const version = useEngineVersion(engine);
  return useMemo(() => {
    void version;
    return engine && scope ? engine.scopeState(scope) : undefined;
  }, [engine, scope, version]);
}

export interface SyncRecovery {
  items: readonly RecoveryItem[];
  retry(groupId: string): MutationCall | null;
  resolveConflict(groupId: string, keepLocal: string[]): MutationCall | null;
  discard(groupId: string): Promise<void>;
  resume(scope: string): void;
  /** Todo lo pendiente y la bandeja, en JSON (soporte, respaldo). */
  exportAll(): string;
}

export function useSyncRecovery(): SyncRecovery {
  const engine = useEngine();
  const version = useEngineVersion(engine);
  return useMemo(() => {
    void version;
    return {
      items: engine?.recoveryItems() ?? [],
      retry: (id) => engine?.retryRecovery(id) ?? null,
      resolveConflict: (id, keep) => engine?.resolveConflict(id, keep) ?? null,
      discard: (id) => engine?.discardRecovery(id) ?? Promise.resolve(),
      resume: (scope) => engine?.resume(scope),
      exportAll: () =>
        JSON.stringify({ pending: engine?.pendingGroups() ?? [], recovery: engine?.recoveryItems() ?? [] }, null, 2),
    };
  }, [engine, version]);
}
